/**
 * Pure scoring and scenario math for RFP-2026-014 (Pillar Pro LED high-bays). Kept free of
 * React and data imports so it can be unit tested on its own.
 */
import { BIDDER_IDS } from './sourcing-types';
import type { BidderId, Shares } from './sourcing-types';

export const CRITERIA = [
  {
    id: 'landedCost',
    label: 'Delivered cost',
    hint: 'Unit price, freight, tariff and duty per fixture',
  },
  {
    id: 'quality',
    label: 'Quality and certifications',
    hint: 'UL and DLC certificates, defect and delivery history, notice before changes',
  },
  {
    id: 'supply',
    label: 'Supply security and capacity',
    hint: 'Declared capacity, site resilience, trade compliance',
  },
  {
    id: 'leadTime',
    label: 'Lead time',
    hint: 'Weeks from purchase order to delivery',
  },
  {
    id: 'sustainability',
    label: 'Sustainability',
    hint: 'Sustainability rating (EcoVadis), recyclable packaging, recycled content',
  },
  {
    id: 'commercial',
    label: 'Commercial terms',
    hint: 'Payment terms, price validity and hold, exceptions',
  },
] as const;

export type CriterionId = (typeof CRITERIA)[number]['id'];

export const CRITERION_IDS: readonly CriterionId[] = CRITERIA.map((c) => c.id);

export type Weights = Record<CriterionId, number>;
export type Scores = Record<CriterionId, number>;

export const DEFAULT_WEIGHTS: Weights = {
  landedCost: 35,
  quality: 20,
  supply: 15,
  leadTime: 10,
  sustainability: 10,
  commercial: 10,
};

export function weightsTotal(weights: Weights): number {
  return CRITERION_IDS.reduce((sum, id) => sum + weights[id], 0);
}

function weightsFromArray(values: number[]): Weights {
  return {
    landedCost: values[0] ?? 0,
    quality: values[1] ?? 0,
    supply: values[2] ?? 0,
    leadTime: values[3] ?? 0,
    sustainability: values[4] ?? 0,
    commercial: values[5] ?? 0,
  };
}

function sharesFromArray(values: number[]): Shares {
  return {
    'sup-aurelia': values[0] ?? 0,
    'sup-northgate': values[1] ?? 0,
    'sup-hanover': values[2] ?? 0,
    'sup-brightpak': values[3] ?? 0,
  };
}

/**
 * Set `values[index]` to `next` (whole number, 0-100) and rescale every other
 * entry proportionally so the whole set still adds up to exactly 100. Uses the
 * largest-remainder method so results stay whole numbers.
 */
export function rebalance(
  values: readonly number[],
  index: number,
  next: number,
): number[] {
  const clamped = Math.min(100, Math.max(0, Math.round(next)));
  const remaining = 100 - clamped;
  const out = values.map(() => 0);
  out[index] = clamped;

  const otherIndexes = values.map((_, i) => i).filter((i) => i !== index);
  if (otherIndexes.length === 0) {
    out[index] = 100;
    return out;
  }

  const othersSum = otherIndexes.reduce((sum, i) => sum + values[i], 0);
  const exact = otherIndexes.map((i) =>
    othersSum > 0
      ? (values[i] / othersSum) * remaining
      : remaining / otherIndexes.length,
  );
  const floors = exact.map((v) => Math.floor(v));
  let leftover = remaining - floors.reduce((sum, v) => sum + v, 0);

  otherIndexes.forEach((valueIndex, k) => {
    out[valueIndex] = floors[k];
  });

  const byRemainder = exact
    .map((v, k) => ({ k, fraction: v - Math.floor(v) }))
    .sort((a, b) => b.fraction - a.fraction || a.k - b.k);
  for (const { k } of byRemainder) {
    if (leftover <= 0) break;
    out[otherIndexes[k]] += 1;
    leftover -= 1;
  }
  return out;
}

export function rebalanceWeights(
  weights: Weights,
  changed: CriterionId,
  next: number,
): Weights {
  const values = CRITERION_IDS.map((id) => weights[id]);
  return weightsFromArray(
    rebalance(values, CRITERION_IDS.indexOf(changed), next),
  );
}

export function rebalanceShares(
  shares: Shares,
  changed: BidderId,
  next: number,
): Shares {
  const values = BIDDER_IDS.map((id) => shares[id]);
  return sharesFromArray(rebalance(values, BIDDER_IDS.indexOf(changed), next));
}

/** Weighted score on the same 0-10 scale as the criterion scores. */
export function weightedTotal(scores: Scores, weights: Weights): number {
  const total = weightsTotal(weights);
  if (total === 0) return 0;
  return CRITERION_IDS.reduce(
    (sum, id) => sum + (scores[id] * weights[id]) / total,
    0,
  );
}

export interface Contribution {
  criterion: CriterionId;
  /** Points this criterion adds to the 0-10 weighted total. */
  points: number;
}

export interface RankedBidder<T extends string> {
  id: T;
  total: number;
  rank: number;
  contributions: Contribution[];
}

export function contributions(
  scores: Scores,
  weights: Weights,
): Contribution[] {
  const total = weightsTotal(weights);
  return CRITERION_IDS.map((criterion) => ({
    criterion,
    points: total === 0 ? 0 : (scores[criterion] * weights[criterion]) / total,
  }));
}

/** Rank bidders by weighted total, highest first. Ties keep input order. */
export function rankBidders<T extends string>(
  entries: readonly { id: T; scores: Scores }[],
  weights: Weights,
): RankedBidder<T>[] {
  const scored = entries.map((entry, order) => ({
    id: entry.id,
    order,
    total: weightedTotal(entry.scores, weights),
    contributions: contributions(entry.scores, weights),
  }));
  scored.sort((a, b) => b.total - a.total || a.order - b.order);
  return scored.map((entry, index) => ({
    id: entry.id,
    total: entry.total,
    rank: index + 1,
    contributions: entry.contributions,
  }));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Landed cost score: 10 for the lowest landed cost, losing 2 points for every
 * 10% above it, floored at 0.
 */
export function landedCostScore(landed: number, lowestLanded: number): number {
  if (lowestLanded <= 0) return 0;
  return round2(clamp(10 - (landed / lowestLanded - 1) * 20, 0, 10));
}

/** Lead time score: 10 at or under the best lead time, minus 1 per extra week. */
export function leadTimeScore(weeks: number, bestWeeks: number): number {
  return round2(clamp(10 - (weeks - bestWeeks), 0, 10));
}

export interface ScenarioPrice {
  /** Landed cost per fixture. */
  landed: number;
  /** Tariff and duty per fixture (already inside landed). */
  tariff: number;
}

export interface ScenarioInputs {
  /** Annual volume in fixtures. */
  volume: number;
  /** Current blended run-rate per fixture. */
  baselinePerUnit: number;
  years: number;
}

export interface ScenarioResult {
  blendedLanded: number;
  annualCost: number;
  threeYearCost: number;
  annualSavings: number;
  /** Savings over the full horizon versus the baseline run-rate. */
  savings: number;
  tariffExposure: number;
  sourceCount: number;
  largestShare: number;
}

export function evaluateScenario(
  shares: Shares,
  prices: Record<BidderId, ScenarioPrice>,
  inputs: ScenarioInputs,
): ScenarioResult {
  const totalShare = BIDDER_IDS.reduce((sum, id) => sum + shares[id], 0);
  const divisor = totalShare === 0 ? 1 : totalShare;
  const blendedLanded = BIDDER_IDS.reduce(
    (sum, id) => sum + (shares[id] / divisor) * prices[id].landed,
    0,
  );
  const blendedTariff = BIDDER_IDS.reduce(
    (sum, id) => sum + (shares[id] / divisor) * prices[id].tariff,
    0,
  );
  const annualCost = blendedLanded * inputs.volume;
  const baselineAnnual = inputs.baselinePerUnit * inputs.volume;
  return {
    blendedLanded,
    annualCost,
    threeYearCost: annualCost * inputs.years,
    annualSavings: baselineAnnual - annualCost,
    savings: (baselineAnnual - annualCost) * inputs.years,
    tariffExposure: blendedTariff * inputs.volume * inputs.years,
    sourceCount: BIDDER_IDS.filter((id) => shares[id] > 0).length,
    largestShare:
      Math.max(...BIDDER_IDS.map((id) => shares[id] / divisor)) * 100,
  };
}
